import { useState, useEffect } from "react";
import { Button } from "@twilio-paste/button";
import { Box } from "@twilio-paste/core";

import { getToken } from "../../api";
import { InputType } from "../../types";
import ModalInputField from "../modals/ModalInputField";
import styles from "../../styles";
import useAppAlert from "../../hooks/useAppAlerts";
import React from "react";
import {
  AppLogo,
  APP_TAGLINE,
  LOGIN_SUB_TITLE,
  LOGIN_TITLE,
  LOGIN_TITLE_SUBLINE,
  LOGO_TITLE,
} from "../../branding";
import { InstallPromptState } from "../../hooks/useInstallPrompt";

type SetTokenType = (token: string) => void;

interface LoginProps {
  setToken: SetTokenType;
  installPrompt: InstallPromptState;
}

async function login(
  username: string,
  password: string,
  setToken: (token: string) => void
): Promise<string> {
  try {
    const token = await getToken(username.trim(), password);
    if (token === "") {
      return "Received an empty token from backend.";
    }

    localStorage.setItem("username", username);
    localStorage.setItem("password", password);
    setToken(token);

    return "";
  } catch (error) {
    let message = "Unknown Error";
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = String(error);
    }
    return message;
  }
}

const Login: React.FC<LoginProps> = (props: LoginProps) => {
  const [isFormDirty, setFormDirty] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [, AlertsView] = useAppAlert();

  const handleLogin = async () => {
    const error = await login(username, password, props.setToken);
    if (error) {
      setFormError(error);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        handleLogin();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyPress);
      abortController.abort();
    };
  }, [password, username]);

  return (
    <Box style={styles.loginContainer}>
      <AlertsView />
      <Box style={styles.loginContent}>
        <div style={styles.loginEyebrow}>SellBot AiML operator workspace</div>
        <Box style={styles.loginLogoShell}>
          <AppLogo title="SellBot AiML" />
        </Box>
        <div style={styles.loginTitle}>{LOGIN_TITLE}</div>
        <div style={styles.loginTitleSubline}>{LOGIN_TITLE_SUBLINE}</div>
        <div style={styles.subTitle}>{LOGIN_SUB_TITLE}</div>
        <Box style={styles.loginModuleStack}>
          <Box style={styles.loginModuleConnector} />
          <Box style={styles.loginForm}>
            <Box
              style={{
                padding: "28px 28px 0",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#3568E5",
                }}
              >
                {LOGO_TITLE}
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 15,
                  lineHeight: "24px",
                  color: "#5A6C84",
                }}
              >
                {APP_TAGLINE}
              </div>
            </Box>
            <Box style={styles.userInput}>
              <ModalInputField
                label="Username"
                placeholder=""
                isFocused={true}
                error={
                  isFormDirty && !username.trim()
                    ? "Enter a username to sign in."
                    : ""
                }
                input={username}
                onChange={(username: string) => {
                  setUsername(username);
                  setFormError("");
                }}
                onBlur={() => {
                  if (password) {
                    setFormDirty(true);
                  }
                }}
                id="username"
              />
            </Box>
            <Box style={styles.passwordInput}>
              <ModalInputField
                label="Password"
                placeholder=""
                error={
                  isFormDirty && !password
                    ? "Enter a password to sign in."
                    : formError ?? ""
                }
                input={password}
                onChange={(password: string) => {
                  setPassword(password);
                  setFormError("");
                }}
                onBlur={() => setFormDirty(true)}
                inputType={showPassword ? InputType.Text : InputType.Password}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                id="password"
              />
            </Box>
            <Box style={styles.loginButton}>
              <Button
                fullWidth
                disabled={!username || !password}
                variant="primary"
                onClick={handleLogin}
                id="login"
              >
                Sign in
              </Button>
            </Box>
            {props.installPrompt.canInstall ? (
              <Box style={styles.loginSecondaryButton}>
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={props.installPrompt.promptInstall}
                >
                  {props.installPrompt.installLabel}
                </Button>
              </Box>
            ) : null}
            {props.installPrompt.isIosManualInstall ? (
              <Box style={styles.installHint}>
                On iPhone or iPad, open this in Safari, tap Share, then choose
                Add to Home Screen. Push notifications work from the installed
                Home Screen app.
              </Box>
            ) : null}
          </Box>
          <Box style={styles.loginModuleDivider}>
            <div style={styles.loginModuleDividerPill}>Operator workflow</div>
          </Box>
          <Box style={styles.loginFeatureGrid}>
            <Box style={styles.loginFeatureCard}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#8CB5FF",
                }}
              >
                Live handoff
              </div>
              <div style={{ marginTop: 8, fontSize: 14, lineHeight: "22px" }}>
                Pick up conversations when AI escalates and a human needs to
                reply.
              </div>
            </Box>
            <Box style={styles.loginFeatureCard}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#7DE0C0",
                }}
              >
                Push-ready
              </div>
              <div style={{ marginTop: 8, fontSize: 14, lineHeight: "22px" }}>
                Stay on top of assignments, mentions, and shopper replies from
                one place.
              </div>
            </Box>
            <Box style={styles.loginFeatureCard}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#FFD08A",
                }}
              >
                Team inbox
              </div>
              <div style={{ marginTop: 8, fontSize: 14, lineHeight: "22px" }}>
                Route work across operators while keeping the storefront
                experience under the SellBot brand.
              </div>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box style={styles.loginBackground}>
        <Box
          style={{
            position: "absolute",
            width: 360,
            height: 360,
            top: -120,
            right: -80,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(125, 224, 192, 0.22) 0%, rgba(125, 224, 192, 0) 70%)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            width: 520,
            height: 520,
            bottom: -220,
            left: -140,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(73, 120, 255, 0.22) 0%, rgba(73, 120, 255, 0) 70%)",
          }}
        />
      </Box>
    </Box>
  );
};

export default Login;
